import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators'
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import * as fromRoot from '../reducers';
import * as engagement from '../actions/engagement';
import * as engage from '../actions/engage';
import { UserService } from '../core/user';
import { AnalyticsService } from '../core/analytics';
import { DiscoverService } from '../discover/discover.service';
import { ErrorHandlerService } from '../core/error-handler';
import { DiscoverConstants } from '../discover/discover.constants';
import { CancellationDialogComponent } from '../discover/cancellation-dialog/cancellation-dialog.component';
import { SendConfirmationDialogComponent } from '../shared/send-confirmation-dialog/send-confirmation-dialog.component';
import { SharedConstants } from '../shared/shared.constants';
import { CalendarService } from '../calendar/calendar.service';
import { environment } from '../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CommunityCareMultipleEventModalComponent
} from '../shared/community-care-multiple-event-modal/community-care-multiple-event-modal.component';

@Injectable()
export class EngagementEffects {

  public fetchEngagementDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.START_ONE_PAGE_FLOW),
    switchMap(() => this.store.select(fromRoot.getEngagementDetailRequest).pipe(take(1))),
    switchMap((req) => this.getEngagementDetails(req).pipe(
      map((res) => new engagement.FetchEngagementDetailsSuccess(res)),
      catchError((error) => of(new engagement.ReceivedEngagementFailed(error)))
    ))
  )});

  public fetchThirdPartyDateRestrictionDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.START_ONE_PAGE_FLOW),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.thirdPartyPromotion),
    switchMap(() => this.getThirdPartyDateRestrictions().pipe(
      map((res) => new engagement.FetchThirdPartyDateRestrictionSuccess(res)),
      catchError((error) => of(new engagement.ReceivedEngagementFailed(error)))
    ))
  )});

  public fetchCommunityCareDateRestrictionDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.START_ONE_PAGE_FLOW),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.categoryCommunityCare),
    switchMap(() => this.store.select(fromRoot.getFeatureEnabled(SharedConstants.MultipleCommunityCareEvents)).pipe(take(1))),
    filter((enabled) => enabled),
    switchMap(() => this.getCommunityCareEventDateRestrictions().pipe(
      map((res) => new engagement.FetchCommunityCareDateRestrictionSuccess(res)),
      catchError((error) => of(new engagement.ReceivedEngagementFailed(error)))
    ))
  )});

  public fetchInternalPromoDateRestrictionDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.START_ONE_PAGE_FLOW),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.promotion || type === DiscoverConstants.freeItemInCartPromotion),
    switchMap(() => this.store.select(fromRoot.getFeatureEnabled(DiscoverConstants.freeItemInCartPromotionFlag)).pipe(take(1))),
    filter((freeInCartEnabled)=>{return freeInCartEnabled}),
    switchMap(() => this.getInternalPromotionsDateRestrictions().pipe(
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      map(([res, location]) => new engagement.FetchInternalPromoDateRestrictionSuccess(res, location.locationNumber)),
      catchError((error) => of(new engagement.ReceivedEngagementFailed(error)))
    ))
  )});

  public createAutomation$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.automation),
    switchMap(() => this.store.select(fromRoot.getAutomationTreatRequest).pipe(take(1))),
    switchMap((eng) => this.sendAutomation(eng).pipe(
      map((res) => new engagement.CreateAutomationSuccess(res)),
      catchError(() => of(new engagement.CreateAutomationFailure('There was an error automating this campaign. Please try again later.')))
    ))
  )});

  public updateAutomation$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.EDIT_AUTOMATION),
    switchMap(() => this.store.select(fromRoot.getAutomationTreatRequest).pipe(take(1))),
    switchMap((eng) => this.editAutomation(eng).pipe(
      map((res) => new engagement.EditAutomationSuccess(res)),
      catchError(() => of(new engagement.EditAutomationFailure('There was an error editing this campaign. Please try again later.')))
    ))
  )});

  public sendReward$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.reward),
    switchMap(() => this.store.select(fromRoot.getOnePageRewardRequest).pipe(take(1))),
    switchMap((eng) => this.sendRewards(eng).pipe(
      map((res) => new engagement.SendRewardSuccess(res)),
      catchError(() => of(new engagement.SendFailure('There was an error sending this campaign. Please try again later.')))
    ))
  )});

  public sendEmail$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.email),
    switchMap(() => this.store.select(fromRoot.getOnePageEmailRequest).pipe(take(1))),
    switchMap((eng) => this.sendEmail(eng).pipe(
      map((res) => new engagement.SendEmailSuccess(res)),
      catchError(() => of(new engagement.SendFailure('There was an error sending this campaign. Please try again later.')))
    ))
  )});

  public sendPromotion$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.promotion),
    switchMap(() => this.store.select(fromRoot.getOnePagePromotionRequest).pipe(take(1))),
    switchMap((eng) => this.sendPromotion(eng).pipe(
      map((res) => new engagement.SendPromotionSuccess(res)),
      catchError(() => of(new engagement.SendFailure('There was an error sending this campaign. Please try again later.')))
    ))
  )});

  public sendThirdPartyPromotion$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.thirdPartyPromotion),
    switchMap(() => this.store.select(fromRoot.getOnePageThirdPartyPromotionRequest).pipe(take(1))),
    switchMap((eng) => this.sendThirdPartyPromotion(eng).pipe(
      map((res) => {
        return res.status === 'failed' ? new engagement.SendThirdPartyPromotionFailure(res.responseObject)
          : new engagement.SendThirdPartyPromotionSuccess(res.responseObject);
      }),
      catchError((err) => of(new engagement.SendThirdPartyPromotionFailure(err)))
    ))
  )});

  public sendFreeInCart$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.freeItemInCartPromotion),
    switchMap(() => this.store.select(fromRoot.getOnePageFreeInCartRequest).pipe(take(1))),
    switchMap((eng) => this.sendFreeInCart(eng).pipe(
      map((res) => { return res.status === 'failed' ? new engagement.SendFreeInCartFailure(res.responseObject) :
        new engagement.SendFreeInCartSuccess(res.responseObject)}),
      catchError((err) => of(new engagement.SendFreeInCartFailure(err)))
    ))
  )});

  public sendCommunityCareEvent$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.SEND_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementType).pipe(take(1))),
    filter((type) => type === DiscoverConstants.categoryCommunityCare),
    switchMap(() => this.store.select(fromRoot.getOnePageCommunityEventRequest).pipe(take(1))),
    switchMap((eng) => this.sendCommunityCareEvent(eng).pipe(
      map((res) => new engagement.SendCommunityCareEventSuccess(res)),
      catchError(() => of(new engagement.SendFailure('There was an error creating this Community Care Event. Please try again later.')))
    ))
  )});

  public stopAutomation$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.STOP_AUTOMATION),
    map((action: any) => action.payload),
    concatLatestFrom(()=> this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    switchMap(([payload, automationFeatureMap]) => this.ds.stopAutomationById(payload, automationFeatureMap).pipe(
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      map(([res, location]) => new engagement.StopAutomationSuccess(payload, location.locationNumber)),
      catchError(() => of(new engagement.StopAutomationFailure(DiscoverConstants.stopAutomationError)))
    ))
  )});

  public stopCommunityCareEvent$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.STOP_COMMUNITY_CARE_EVENT),
    map((action: any) => action.payload),
    switchMap((payload) => this.ds.stopCommunityCareEventById(payload).pipe(
      map(() => new engagement.StopCommunityCareEventSuccess(DiscoverConstants.communityCareStopEventSuccess)),
      catchError(() => of(new engagement.StopCommunityCareEventFailure(
        DiscoverConstants.communityCareStopEventError)))
    ))
  )});

  public stopFreeInCart$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.STOP_FREE_IN_CART),
    map((action: any) => {
      let payload = action.payload
      payload.fieldId = payload.id
      payload.id = DiscoverConstants.freeItemInCartPromotion
      payload.automated = false
      return payload
    }),
    switchMap((payload) => this.ds.stopFreeItemInCartById(payload).pipe(
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      map(([res, location]) =>
        new engagement.StopFreeInCartSuccess(payload, location.locationNumber)),
      catchError(() => of(new engagement.StopFreeInCartFailure(
        DiscoverConstants.freeItemInCartError)))
    ))
  )});

  public cancelThirdPartyPromotion$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION),
    map((action: any) => action.payload),
    switchMap((payload) => this.ds.stopThirdPartyPromotionById(payload).pipe(
      map((cancelEvent) => new engagement.CancelThirdPartyPromotionSuccess(cancelEvent)),
      catchError((error) => of(new engagement.CancelThirdPartyPromotionError(error))))
    )
  )});

  public stopAutomationFailure$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.STOP_AUTOMATION_FAILURE),
    tap(() => {
      // Only go to error page if the automation was cancelled from the one page flow
      if (this.router.url.split('?')[0].endsWith('/discover/edit')) {
        this.router.navigate(['/discover/error']);
      }
    })
  )}, { dispatch: false });

  public openCancellationDialog$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.OPEN_CANCELLATION_DIALOG),
    map((action: any) => action.payload.tile),
    tap((tile: any) => {
      let config = new MatDialogConfig();
      config.autoFocus=false;
      if (tile.id === DiscoverConstants.bonusPoint && (tile.tileObjectId || tile.fieldId)) {
        config.data = {
          title: tile.automationCancellationTitle,
          displayText: tile.automationCancellationBody,
          id: tile.id,
          fieldId: tile.tileObjectId ? tile.tileObjectId : tile.fieldId
        };
      } else if (tile.id === DiscoverConstants.categoryCommunityCare || tile.id === DiscoverConstants.communityCareEventTileId) {
        config.data = {
          eventId: tile.tileObjectId,
          title: 'This will STOP your Community Care Event',
          displayText: 'You can activate a new event at any time through Discover.',
          id: tile.id
        };
      } else if (tile.id === DiscoverConstants.thirdPartyPromotion) {
        config.data = {
          eventId: tile.tileObjectId,
          title: 'This will STOP your Third Party Promotion',
          displayText: 'Note: If this promotion is currently running on multiple third-party platforms, ' +
            'it will be stopped on all platforms.',
          id: tile.id
        };
      } else if (tile.id === DiscoverConstants.freeItemInCartPromotion) {
        config.data = {
          fieldId: tile.tileObjectId,
          title: tile.automationCancellationTitle,
          displayText: tile.automationCancellationBody,
          id: tile.id
        };
      } else {
        config.data = {
          title: tile.automationCancellationTitle,
          displayText: tile.automationCancellationBody,
          id: tile.id,
          fieldId: tile.fieldId ?? ''
        };
      }
      config.panelClass = 'dialog-responsive';
      this.dialog.open(CancellationDialogComponent, config);
    })
  )}, { dispatch: false });

  public startOnePageFlow$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.START_ONE_PAGE_FLOW),
    map((action: any) => action.payload),
    tap((payload) => {
      let route: string;
      if (payload.engagementType=== DiscoverConstants.thirdPartyPromotion) {
        route = '/discover/edit/edit-third-party-promotion';
      } else if (payload.engagementType === DiscoverConstants.promotion) {
        route = '/discover/edit/edit-promotion-details';
      } else if (payload.engagementType === DiscoverConstants.categoryCommunityCare) {
        route = '/discover/edit/edit-community-care-detail';
      } else if (payload.engagementType === DiscoverConstants.freeItemInCartPromotion) {
        route = '/discover/edit/edit-free-item-in-cart-details';
      } else {
        route = '/discover/edit/edit-details';
      }
      // A deep link is already navigating here; the route guard dispatched this to fill in state.
      const pendingUrl = this.router.currentNavigation()?.extractedUrl?.toString();
      if (pendingUrl?.split('?')[0] === route) {
        return;
      }
      this.router.navigate([route]);
    })
  )},{ dispatch : false } );

  public goToDiscoverPage$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.GO_TO_DISCOVER_PAGE),
    tap(() => {
      this.dialog.closeAll();
      this.router.navigate(['/discover']);
    })
  )}, { dispatch : false } );

  public OpenEngageDialog: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      engagement.ActionTypes.SEND_ENGAGEMENT,
      engagement.ActionTypes.EDIT_AUTOMATION,
      engagement.ActionTypes.STOP_COMMUNITY_CARE_EVENT_SUCCESS,
      engagement.ActionTypes.STOP_COMMUNITY_CARE_EVENT_FAILURE,
      engagement.ActionTypes.STOP_AUTOMATION,
      engagement.ActionTypes.STOP_FREE_IN_CART,
    ),
    map(() => this.openEngagementSendDialog())
  )}, { dispatch: false } );

  public getCalendarEventsForCommunityCare$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.GO_TO_COMMUNITY_OVERLAPPING_ENGAGEMENT),
    switchMap(() => this.store.select(fromRoot.getEngagementCalendarPayload).pipe(take(1))),
    concatLatestFrom(() => this.store.select(fromRoot.getOverlappingEngagements)),
    filter(([engagementDates, overlappingEngagements]) =>
      overlappingEngagements.engagements.length === 0),
    switchMap(([engagementDates, overlappingEngagements]) =>
      this.cs.getCalendarEvents(engagementDates).pipe(
        map((res) => new engage.CalendarEventsFound(res)),
        catchError((error) => of(new engage.CalendarEventsError(error)))
      ))
  )});

  public openCommunityCareMultipleEventsDialog$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.FETCH_COMMUNITY_CARE_DATE_RESTRICTION_SUCCESS),
    switchMap(() => this.store.select(fromRoot.getEngagementCCEModalAlreadyShown).pipe(take(1))),
    switchMap((modalShown) => {
      return modalShown ? EMPTY : this.store.select(fromRoot.getSortedAndShortenedCommunityCareEvents).pipe(take(1))
    }),
    concatLatestFrom(() => this.store.select(fromRoot.getIsMobile)),
    map(([events, isMobile]) => {
      if (events && events.length > 0) {
        this.openCommunityCareMultipleEventsDialog(events, isMobile);
      }
    })
  )}, { dispatch : false } );

  public cancelThirdPartySuccess$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION_SUCCESS,),
    tap(() => {
      this.dialog.closeAll();
      this.snackBar.open(
        `Promotion was successfully deleted.`,
        'Dismiss', {
          duration: 5000
        }
      );
      this.router.navigate(['/discover']);
    })
  )}, { dispatch : false });

  public cancelThirdPartyFailure$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(engagement.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION_ERROR,),
    tap(() => {
      this.dialog.closeAll();
      this.snackBar.open(
        `Promotion failed to delete. Please try again later.`,
        'Dismiss', {
          duration: 5000
        }
      );
      this.router.navigate(['/discover']);
    })
  )}, { dispatch : false });

  constructor(
    private actions$: Actions,
    private store: Store<fromRoot.State>,
    private us: UserService,
    private ds: DiscoverService,
    private analytics: AnalyticsService,
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private ehs: ErrorHandlerService,
    private cs: CalendarService,
  ) {

  }

  private sendEmail(emailReq): Observable<any> {
    this.sendAnalytics({
      flowId: emailReq.flowId
    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/sendEmail`;
        return this.http.post(environment.spotlightApiUrl + url, emailReq);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendRewards(rewardRequest): Observable<any> {
    this.sendAnalytics({
      flowId: rewardRequest.recommendationType
    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/sendTreats`;
        return this.http.post(environment.spotlightApiUrl + url, rewardRequest);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendPromotion(promotionRequest): Observable<any> {
    let destinations = ''
    promotionRequest.destinations.forEach(des => { destinations = destinations + des + ', ' })
    this.sendAnalytics({
      flowId: promotionRequest.flowId,
      startTime: promotionRequest.startTime,
      startDate: promotionRequest.startDate,
      endTime: promotionRequest.endTime,
      endDate: promotionRequest.endDate,
      destinations: destinations,
      repeatOption: promotionRequest.repeatOption.repeatOptionId,
      subOption: promotionRequest.repeatOption.subOptions ? promotionRequest.repeatOption.subOptions[0].id : '',
      timeOfDay: promotionRequest.timeOfDayId
    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/promotion`;
        return this.http.post(environment.spotlightApiUrl + url, promotionRequest);
      }),
      map((res: any) => {
        let automation = res.responseObject;
        automation.automated = true;
        automation.fieldId = automation.id;
        return automation;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendThirdPartyPromotion(promotionRequest): Observable<any> {
    let platforms = '';
    promotionRequest.platforms.forEach(platform => {platforms = platforms + platform + ', '})
    this.sendAnalytics({
      flowId: promotionRequest.flowId,
      startTime: promotionRequest.startTime,
      startDate: promotionRequest.startDate,
      endTime: promotionRequest.endTime,
      endDate: promotionRequest.endDate,
      reward: promotionRequest.offerTemplateId,
      platforms: platforms,
      customerType: promotionRequest.customerType ? promotionRequest.customerType[0] : '',

    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/createThirdPartyPromotion`;
        return this.http.post( environment.spotlightApiUrl + url, promotionRequest);
      }),
      map((res: any) => {
        let automation = res.responseObject;
        automation.automated = true;
        automation.fieldId = automation.id;
        res.responseObject = automation
        return res;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendFreeInCart(freeInCartRequest): Observable<any> {
    let destinations = ''
    freeInCartRequest.destinationIds.forEach(des => {destinations = destinations + des +', '})
    this.sendAnalytics({
      flowId: freeInCartRequest.flowId,
      startTime: freeInCartRequest.startTime,
      startDate: freeInCartRequest.startDate,
      endTime: freeInCartRequest.endTime,
      endDate: freeInCartRequest.endDate,
      customerType: freeInCartRequest.customerType ? freeInCartRequest.customerType[0] : '',
      timeOfDay: freeInCartRequest.timeOfDayId,
      repeatOption: freeInCartRequest.repeatOption.repeatOptionId,
      subOption: freeInCartRequest.repeatOption.subOptions ? freeInCartRequest.repeatOption.subOptions[0].id : '',
      destinations: destinations,
      reward: freeInCartRequest.offerTemplateId
    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/createFreeInCartPromotion`;
        return this.http.post( environment.spotlightApiUrl + url, freeInCartRequest);
      }),
      map((res: any) => {
        let automation = res.responseObject;
        automation.automated = true;
        automation.fieldId = automation.id;
        res.responseObject = automation
        return res;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendCommunityCareEvent(communityCareEventRequest): Observable<any> {
    let orgs = '';
    let codes = ''
    communityCareEventRequest.engagementOrganizations.forEach(org => {
      orgs = orgs + org.organizationName + ', '
      codes = codes + org.organizationCode + ', '
    } )
    this.sendAnalytics({
      flowId: communityCareEventRequest.flowId,
      startTime: communityCareEventRequest.startTime,
      startDate: communityCareEventRequest.startDate,
      endTime: communityCareEventRequest.endTime,
      endDate: communityCareEventRequest.endDate,
      orgs: orgs,
      optingIn: communityCareEventRequest.optingIn,
      codes: codes
    });
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/location/${locationNo}/communityCareEvent`;
        if (communityCareEventRequest.startDate == null || communityCareEventRequest.endDate == null) {
          throw new Error('error creating cce')
        }
        return this.http.post(environment.spotlightApiUrl + url, communityCareEventRequest) ;
      }),
      map((res: any) => {
        return res.responseObject;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendAutomation(automationReq): Observable<any> {
    this.analytics.sendEvent(
      'Automation Flow Sent',
      {
        'Flow ID': automationReq.id,
        'Include Active Treats': automationReq.includeActiveTreats,
        'Treat Validity Length': automationReq.treatValidityDays,
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        automationReq = { ...automationReq, locationNumber: loc.locationNumber };
        let url = `/api/location/${loc.locationNumber}/automation`;
        return this.http.post(environment.spotlightApiUrl + url, automationReq);
      }),
      map((res: any) => {
        let automation = res.responseObject;
        automation.automated = true;
        return automation;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private editAutomation(automationReq): Observable<any> {
    this.analytics.sendEvent(
      'Automation Edited',
      {
        'Flow ID': automationReq.id,
        'Include Active Treats': automationReq.includeActiveTreats,
        'Treat Validity Length': automationReq.treatValidityDays,
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        automationReq = { ...automationReq, locationNumber: loc.locationNumber };
        let url = `/api/location/${loc.locationNumber}/automation`;
        return this.http.put(environment.spotlightApiUrl + url, automationReq);
      }),
      map((res: any) => {
        let automation = res.responseObject;
        automation.automated = true;
        return automation;
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private getEngagementDetails(treatRequest): Observable<any> {
    const shouldGetAutomationDetails = treatRequest.engagementMode === DiscoverConstants.edit;
    const treatRequestCopy = treatRequest;

    if (treatRequest.engagementType === DiscoverConstants.categoryCommunityCare) {
      treatRequest = {
        'engagementType': 'promotion', 'flowId': 'bonus-point', 'campaignName': 'Bonus Point Promotion', 'emailMessage': '',
        'selectedCustomers': [], 'subject': ''
      };
    }
    let result = {
      'status': 'success',
      'responseObject': {
        'communityCaresMetaData': {
          'optInOptions': [{
            'name': 'No Code Required',
            'id': 'noCode',
            'defaultToggle': true,
            'subOpt': null
          }, {
            'name': 'Organization Code',
            'id': 'organizationCode',
            'defaultToggle': false,
            'subOpt': null
          }]
        }
      },
      'statusCode': 200,
      'sortColumnName': null
    }
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        let locationNo = loc.locationNumber;
        let url = `/api/${locationNo}/engagementDetails?automationDetails=${shouldGetAutomationDetails}`;
        return this.http.post(environment.spotlightApiUrl + url, treatRequest);
      }),
      map((res: any) => {
        if (treatRequestCopy.engagementType === DiscoverConstants.categoryCommunityCare) {
          return result.responseObject;
        } else {
          return res.responseObject;
        }
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private getThirdPartyDateRestrictions(): Observable<any> {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const locationNumber = loc.locationNumber;
        const eventType = DiscoverConstants.thirdPartyEventType;
        let url = `/api/location/${locationNumber}/dateRestrictions/${eventType}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private getCommunityCareEventDateRestrictions(): Observable<any> {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const locationNumber = loc.locationNumber;
        const eventType = DiscoverConstants.communityCareEventType;
        let url = `/api/location/${locationNumber}/dateRestrictions/${eventType}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private getInternalPromotionsDateRestrictions(): Observable<any> {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const locationNumber = loc.locationNumber;
        const eventType = DiscoverConstants.internalPromotionType;
        let url = `/api/location/${locationNumber}/dateRestrictions/${eventType}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  private sendAnalytics(payload) {
    this.analytics.sendEvent(
      'Discover Flow Sent',
      payload
    );
  }

  private openEngagementSendDialog() {
    let config: MatDialogConfig = {
      autoFocus: false,
      disableClose: true,
      panelClass:'dialog-responsive',
      data: { page: SharedConstants.discoverPage }
    }
    this.dialog.open(SendConfirmationDialogComponent, config);
  }

  private openCommunityCareMultipleEventsDialog(data, isMobile) {
    let configMobile: MatDialogConfig = {
      autoFocus: false,
      height: '90%',
      panelClass: 'dialog-responsive',
      data: { events: data , isMobile },
    }
    let configDesktop: MatDialogConfig = {
      autoFocus: false,
      data: { events: data , isMobile },
      panelClass: 'dialog-responsive'
    }
    let config = isMobile ? configMobile : configDesktop
    this.dialog.open(CommunityCareMultipleEventModalComponent, config).afterClosed().subscribe(()=>{
      this.store.dispatch(new engagement.CCEUpdateModalShown());
    });
  }
}
